/**
 * PromotionStackPanel
 *
 * Replaces the legacy single-shot ListingPromotion picker on
 * BusinessDashboard and powers the per-event promotion picker on
 * EventEditorWizard's "Manage promotions" entry point.
 *
 * Responsibilities:
 *  - Show every promotion stack already attached to the target (active +
 *    scheduled), with start/end in the user's timezone and a Cancel button
 *    that's only enabled while the stack is scheduled.
 *  - Tier selector (boost/spotlight/featured/premium) with a Count stepper
 *    (1–30) showing live duration + cost preview.
 *  - Custom-range mode with explicit start + end datetimes (in the user's
 *    timezone, stored as UTC).
 *  - "Add to cart" routes through `PromotionCartApi.add` — actual checkout
 *    is performed on the dedicated Cart page.
 *
 * Server-side `priceItem()` (functions/promotionCartFn.js) is the source of
 * truth — client cost numbers shown here are advisory and recomputed on
 * add / checkout.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, query } from "firebase/firestore";
import { firestore } from "../../FirebaseConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faStar,
  faBullhorn,
  faCrown,
  faCalendarPlus,
  faCartPlus,
  faShoppingCart,
  faTimesCircle,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faSync,
  faPlayCircle,
  faCalendarAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  PROMOTION_TIERS,
  PROMOTION_TIER_INFO,
  PROMOTION_TYPE_KEYS,
  PROMOTION_STACK_COUNT_MAX,
  canonicalStackCost,
  canonicalStackDurationDays,
  customStackCost,
} from "../../constants/promotionTiers";
import { Modal, ModalHeader, ModalBody, MODAL_SIZES } from "../ui/Modal/Modal";
import {
  detectBrowserTimezone,
  formatInUserTz,
  toUtcFromLocal,
  toLocalInputValue,
  nextBusinessDayStartUtc,
  daysBetween,
} from "../../utils/datetime";
import { getRecurrenceOptions } from "../../constants/promotionRecurrence";
import { PromotionCartApi } from "../../services/PromotionCartApi";
import { groupStacks, tsToMs, formatCountdown } from "../../utils/promotionStacks";
import usePromotionCartCount from "../../hooks/usePromotionCartCount";
import { useAuth } from "../../contexts/CombinedAuthContext";
import TokenSpendIndicator from "../TokenSpendIndicator";
import "../../styles/PromotionStackPanel.css";

const TIER_ICON = {
  boost: faRocket,
  spotlight: faStar,
  featured: faBullhorn,
  premium: faCrown,
};

function tierIcon(type) {
  return TIER_ICON[type] || faRocket;
}

/**
 * @param {object} props
 * @param {"businessListing"|"event"} props.targetType
 * @param {string} props.targetId
 * @param {object} [props.target]            parent doc data (for derived promotion view)
 * @param {string} [props.userTimezone]      IANA tz, falls back to browser
 * @param {() => void} [props.onCartUpdated] fired after Add succeeds
 */
export default function PromotionStackPanel({
  targetType,
  targetId,
  target,
  userTimezone,
  onCartUpdated,
}) {
  const tz = userTimezone || detectBrowserTimezone();
  const { firebaseUser } = useAuth();
  const cartCount = usePromotionCartCount(firebaseUser?.uid);

  const [stacks, setStacks] = useState([]);
  const [stacksLoading, setStacksLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Tick every 60s so countdown labels stay fresh without manual refresh.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Live subscription to the target's promotionStacks subcollection.
  useEffect(() => {
    if (!targetType || !targetId) return undefined;
    const parentCollection = targetType === "event" ? "events" : "businessListings";
    setStacksLoading(true);
    const q = query(
      collection(firestore, parentCollection, targetId, "promotionStacks"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const sa = tsToMs(a.startAt);
          const sb = tsToMs(b.startAt);
          return sa - sb;
        });
        setStacks(list);
        setStacksLoading(false);
      },
      (err) => {
        setError(err.message || String(err));
        setStacksLoading(false);
      },
    );
    return () => unsub();
  }, [targetType, targetId]);

  // ----- Picker form state -----
  const [pickerMode, setPickerMode] = useState("canonical"); // "canonical" | "custom"
  const [pickerType, setPickerType] = useState("boost");
  const [pickerCount, setPickerCount] = useState(1);

  // Tier info modal. null = closed; otherwise a key from PROMOTION_TIER_INFO
  // ("boost" | "spotlight" | "featured" | "premium" | "custom").
  const [infoTierKey, setInfoTierKey] = useState(null);
  const openTierInfo = useCallback((key) => setInfoTierKey(key), []);
  const closeTierInfo = useCallback(() => setInfoTierKey(null), []);

  // Recurrence picker state. "" = one-off purchase.
  const [recurrenceKey, setRecurrenceKey] = useState("");
  const [recurrenceUntilLocal, setRecurrenceUntilLocal] = useState("");

  // Active promotion type drives which recurrence menu we show.
  const activePromotionType = pickerMode === "custom" ? "custom" : pickerType;
  const recurrenceOptions = useMemo(
    () => getRecurrenceOptions(targetType, activePromotionType),
    [targetType, activePromotionType],
  );
  // Reset the recurrence selection if the user switches to a type that doesn't
  // offer the previously-picked option.
  useEffect(() => {
    if (!recurrenceKey) return;
    if (!recurrenceOptions.some((o) => o.key === recurrenceKey)) {
      setRecurrenceKey("");
      setRecurrenceUntilLocal("");
    }
  }, [recurrenceKey, recurrenceOptions]);

  // For canonical stacks the user picks a start date; end is derived.
  const defaultStart = useMemo(() => new Date(nextBusinessDayStartUtc(tz)), [tz]);
  const [startLocal, setStartLocal] = useState(() => toLocalInputValue(defaultStart, tz));
  const [endLocal, setEndLocal] = useState(() => {
    const seedEnd = new Date(defaultStart.getTime() + PROMOTION_TIERS.boost.durationDays * 86400000);
    return toLocalInputValue(seedEnd, tz);
  });

  const [submitting, setSubmitting] = useState(false);

  // Derived preview numbers (advisory — server recomputes on add).
  // toUtcFromLocal returns a Date; normalize to ms here so downstream arithmetic
  // (e.g. startUtcMs + days * 86400000) doesn't stringify a Date.
  const startUtcMs = useMemo(() => {
    try { return toUtcFromLocal(startLocal, tz)?.getTime() || 0; } catch { return 0; }
  }, [startLocal, tz]);
  const endUtcMs = useMemo(() => {
    if (pickerMode !== "custom") return 0;
    try { return toUtcFromLocal(endLocal, tz)?.getTime() || 0; } catch { return 0; }
  }, [pickerMode, endLocal, tz]);

  const previewCost = useMemo(() => {
    if (pickerMode === "custom") {
      const days = daysBetween(startUtcMs, endUtcMs);
      return customStackCost("boost", days);
    }
    return canonicalStackCost(pickerType, pickerCount);
  }, [pickerMode, pickerType, pickerCount, startUtcMs, endUtcMs]);

  const previewDays = useMemo(() => {
    if (pickerMode === "custom") return daysBetween(startUtcMs, endUtcMs);
    return canonicalStackDurationDays(pickerType, pickerCount);
  }, [pickerMode, pickerType, pickerCount, startUtcMs, endUtcMs]);

  const previewEndUtcMs = useMemo(() => {
    if (pickerMode === "custom") return endUtcMs;
    return startUtcMs + previewDays * 86400000;
  }, [pickerMode, startUtcMs, previewDays, endUtcMs]);

  const validationError = useMemo(() => {
    if (!startUtcMs) return "Enter a start date and time.";
    if (pickerMode === "custom") {
      if (!endUtcMs) return "Enter an end date and time.";
      if (endUtcMs <= startUtcMs) return "End must be after start.";
    }
    if (pickerMode === "canonical") {
      if (pickerCount < 1 || pickerCount > PROMOTION_STACK_COUNT_MAX) {
        return `Count must be between 1 and ${PROMOTION_STACK_COUNT_MAX}.`;
      }
    }
    return null;
  }, [pickerMode, startUtcMs, endUtcMs, pickerCount]);

  const handleAdd = useCallback(async () => {
    if (validationError) return;
    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      const params = {
        targetType,
        targetId,
        promotionType: pickerMode === "custom" ? "custom" : pickerType,
        count: pickerMode === "custom" ? 1 : pickerCount,
        startAt: new Date(startUtcMs).toISOString(),
      };
      if (pickerMode === "custom") {
        params.endAt = new Date(endUtcMs).toISOString();
      }
      // Attach recurrence metadata when the user picked a repeat cadence.
      // Server stores the rrule + optional UNTIL; cron materializes occurrences.
      const recurrenceChoice = recurrenceOptions.find((o) => o.key === recurrenceKey);
      if (recurrenceChoice) {
        let untilIso = null;
        if (recurrenceUntilLocal) {
          try {
            const untilMs = toUtcFromLocal(recurrenceUntilLocal, tz);
            if (untilMs && untilMs > startUtcMs) {
              untilIso = new Date(untilMs).toISOString();
            }
          } catch { /* ignore parse failure — server can also validate */ }
        }
        params.recurrence = {
          rrule: recurrenceChoice.rrule,
          ...(untilIso ? { until: untilIso } : {}),
        };
      }
      const res = await PromotionCartApi.add(params);
      setFeedback({
        kind: "success",
        message: `Added to cart — ${res?.item?.tokenCost ?? previewCost} Doubloons.`,
      });
      if (onCartUpdated) onCartUpdated();
    } catch (err) {
      setError(err?.message || "Failed to add to cart.");
    } finally {
      setSubmitting(false);
    }
  }, [validationError, targetType, targetId, pickerMode, pickerType, pickerCount, startUtcMs, endUtcMs, previewCost, onCartUpdated, recurrenceKey, recurrenceOptions, recurrenceUntilLocal, tz]);

  const handleCancel = useCallback(async (stack) => {
    if (!window.confirm(`Cancel this scheduled ${stack.type} promotion? Tokens will be refunded.`)) return;
    try {
      await PromotionCartApi.cancelStack({ targetType, targetId, stackId: stack.id });
      setFeedback({ kind: "success", message: "Stack cancelled — tokens refunded." });
    } catch (err) {
      setError(err?.message || "Failed to cancel.");
    }
  }, [targetType, targetId]);

  // Render stacks split into "Active now" / "Scheduled" / "Past".
  const groupedStacks = useMemo(() => groupStacks(stacks), [stacks]);
  const derivedActive = target?.promotion?.isActive ? target.promotion : null;

  return (
    <div className="psp">
      <header className="psp__header">
        <div className="psp__header-text">
          <h3 className="psp__title">
            <FontAwesomeIcon icon={faBullhorn} /> Promotions
          </h3>
          <p className="psp__subtitle">
            Schedule one or more promotion stacks. Build a cart, then check out from
            {" "}
            <Link to="/promotions/cart">your cart</Link>.
          </p>
        </div>
        <TokenSpendIndicator
          userId={firebaseUser?.uid}
          src="promotion-stack-panel"
          className="psp__token-indicator"
        />
      </header>

      {error && (
        <div className="psp__alert psp__alert--error" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}
      {feedback && (
        <div className={`psp__alert psp__alert--${feedback.kind}`}>
          <FontAwesomeIcon icon={faCheckCircle} /> {feedback.message}
        </div>
      )}

      <ExistingStacksSection
        stacksLoading={stacksLoading}
        stacks={stacks}
        groupedStacks={groupedStacks}
        derivedActive={derivedActive}
        tz={tz}
        onCancel={handleCancel}
      />

      <section className="psp__section">
        <h4 className="psp__section-title">Add a new promotion</h4>
        <div className="psp__mode-toggle" role="tablist" aria-label="Promotion mode">
          <button
            type="button"
            role="tab"
            aria-selected={pickerMode === "canonical"}
            className={`psp__mode-toggle__opt ${pickerMode === "canonical" ? "active" : ""}`}
            onClick={() => setPickerMode("canonical")}
          >
            Standard tier
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={pickerMode === "custom"}
            className={`psp__mode-toggle__opt ${pickerMode === "custom" ? "active" : ""}`}
            onClick={() => setPickerMode("custom")}
          >
            Custom dates
          </button>
          <button
            type="button"
            className="psp__mode-info-btn"
            onClick={() => openTierInfo(pickerMode === "custom" ? "custom" : pickerType)}
            aria-label={`Learn how the ${pickerMode === "custom" ? "Custom" : (PROMOTION_TIERS[pickerType]?.label || "selected")} promotion works`}
            title="How does this promotion work?"
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </button>
        </div>

        {pickerMode === "canonical" ? (
          <div className="psp__tier-grid">
            {PROMOTION_TYPE_KEYS.map((key) => {
              const tier = PROMOTION_TIERS[key];
              const selected = pickerType === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`psp__tier ${selected ? "selected" : ""}`}
                  onClick={() => setPickerType(key)}
                  aria-pressed={selected}
                >
                  {/* Info affordance — uses a span+role to avoid nesting a
                      <button> inside the tier <button>. */}
                  <span
                    className="psp__tier-info-btn"
                    role="button"
                    tabIndex={0}
                    aria-label={`Learn how the ${tier.label} promotion works`}
                    title="How does this promotion work?"
                    onClick={(e) => { e.stopPropagation(); openTierInfo(key); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        openTierInfo(key);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <span className="psp__tier-icon">
                    <FontAwesomeIcon icon={tierIcon(key)} />
                  </span>
                  <span className="psp__tier-name">{tier.label}</span>
                  <span className="psp__tier-line">
                    {tier.tokenCost} Doubloons · {tier.durationDays}d
                  </span>
                  <span className="psp__tier-blurb">{tier.blurb}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="psp__form-row">
          <label className="psp__field">
            <span>Start (in {tz})</span>
            <input
              type="datetime-local"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
            />
          </label>
          {pickerMode === "custom" ? (
            <label className="psp__field">
              <span>End (in {tz})</span>
              <input
                type="datetime-local"
                value={endLocal}
                onChange={(e) => setEndLocal(e.target.value)}
              />
            </label>
          ) : (
            <label className="psp__field">
              <span>Count (stacks)</span>
              <input
                type="number"
                min={1}
                max={PROMOTION_STACK_COUNT_MAX}
                value={pickerCount}
                onChange={(e) => setPickerCount(Number(e.target.value) || 1)}
              />
            </label>
          )}
        </div>

        {recurrenceOptions.length > 0 && (
          <div className="psp__form-row">
            <label className="psp__field">
              <span>Repeat (optional)</span>
              <select
                value={recurrenceKey}
                onChange={(e) => setRecurrenceKey(e.target.value)}
              >
                <option value="">One-time only</option>
                {recurrenceOptions.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </label>
            {recurrenceKey && (
              <label className="psp__field">
                <span>Repeat until (optional)</span>
                <input
                  type="datetime-local"
                  value={recurrenceUntilLocal}
                  onChange={(e) => setRecurrenceUntilLocal(e.target.value)}
                />
              </label>
            )}
          </div>
        )}

        <div className="psp__preview">
          <div>
            <FontAwesomeIcon icon={faClock} /> {previewDays || 0} day
            {previewDays === 1 ? "" : "s"}{" "}
            (ends {previewEndUtcMs ? formatInUserTz(new Date(previewEndUtcMs), tz, {
              dateStyle: "medium",
              timeStyle: "short",
            }) : "—"})
          </div>
          <div className="psp__preview-cost">
            <strong>{previewCost}</strong> Doubloons
          </div>
        </div>

        {recurrenceKey && (
          <p className="psp__validation psp__validation--info">
            This stack will repeat ({recurrenceOptions.find((o) => o.key === recurrenceKey)?.label.toLowerCase()})
            {recurrenceUntilLocal ? ` until ${recurrenceUntilLocal.replace("T", " ")}` : " until you cancel"}.
            Each occurrence charges tokens at the time it materializes.
          </p>
        )}

        {validationError && (
          <p className="psp__validation">{validationError}</p>
        )}

        <div className="psp__actions">
          <button
            type="button"
            className="psp__add-btn"
            onClick={handleAdd}
            disabled={!!validationError || submitting}
          >
            <FontAwesomeIcon icon={faCartPlus} /> {submitting ? "Adding…" : "Add to cart"}
          </button>
          {cartCount > 0 && (
            <Link to="/promotions/cart" className="psp__open-cart-btn">
              <FontAwesomeIcon icon={faShoppingCart} /> Open cart ({cartCount})
            </Link>
          )}
        </div>
      </section>

      <TierInfoModal tierKey={infoTierKey} onClose={closeTierInfo} />
    </div>
  );
}

/**
 * Modal explaining how a promotion tier (boost/spotlight/featured/premium)
 * or the custom-dates mode affects placement, ranking, and stacking.
 */
function TierInfoModal({ tierKey, onClose }) {
  const info = tierKey ? PROMOTION_TIER_INFO[tierKey] : null;
  const tier = tierKey && tierKey !== "custom" ? PROMOTION_TIERS[tierKey] : null;
  return (
    <Modal
      isOpen={Boolean(info)}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      className="psp__tier-info-modal"
    >
      {info && (
        <>
          <ModalHeader
            title={info.label}
            icon={tierKey === "custom" ? faCalendarAlt : tierIcon(tierKey)}
            subtitle={
              tier
                ? `${tier.tokenCost} Doubloons · ${tier.durationDays} day${tier.durationDays === 1 ? "" : "s"}`
                : "Custom start + end window"
            }
          />
          <ModalBody>
            <p className="psp__tier-info-desc">{info.description}</p>
            {Array.isArray(info.bullets) && info.bullets.length > 0 && (
              <ul className="psp__tier-info-bullets">
                {info.bullets.map((b, i) => (
                  <li key={i}>
                    <FontAwesomeIcon icon={faCheckCircle} /> <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {info.bestFor && (
              <p className="psp__tier-info-bestfor">
                <strong>Best for:</strong> {info.bestFor}
              </p>
            )}
          </ModalBody>
        </>
      )}
    </Modal>
  );
}

function ExistingStacksSection({
  stacksLoading,
  stacks,
  groupedStacks,
  derivedActive,
  tz,
  onCancel,
}) {
  if (stacksLoading) {
    return (
      <section className="psp__section">
        <h4 className="psp__section-title">Promotion schedule</h4>
        <p className="psp__empty">Loading…</p>
      </section>
    );
  }

  const hasActive = groupedStacks.active.length > 0;
    const hasPast = groupedStacks.past.length > 0;

  if (!stacks.length && !derivedActive) {
    return (
      <section className="psp__section">
        <h4 className="psp__section-title">Promotion schedule</h4>
        <div className="psp__schedule-empty">
          <FontAwesomeIcon icon={faCalendarAlt} />{" "}
          No active or scheduled promotions yet. Use the form below to add one.
        </div>
      </section>
    );
  }

  return (
    <section className="psp__section">
      <StackCard
        variant="active"
        title="Active Promotions"
        eyebrow="Live now"
        icon={faPlayCircle}
        items={groupedStacks.active}
        derivedActive={!hasActive ? derivedActive : null}
        tz={tz}
        onCancel={onCancel}
      />
      <StackCard
        variant="scheduled"
        title="Scheduled Promotions"
        eyebrow="Upcoming"
        icon={faCalendarAlt}
        items={groupedStacks.scheduled}
        tz={tz}
        onCancel={onCancel}
      />
      {hasPast && (
        <details className="psp__past-details">
          <summary>Past promotions ({groupedStacks.past.length})</summary>
          <PastStackList items={groupedStacks.past} tz={tz} />
        </details>
      )}
    </section>
  );
}

function StackCard({
  variant,
  title,
  eyebrow,
  icon,
  items,
  derivedActive,
  tz,
  onCancel,
}) {
  const isActive = variant === "active";
  const empty = items.length === 0 && !derivedActive;
  return (
    <div className={`psp__stack-card psp__stack-card--${variant}`}>
      <div className="psp__stack-card-header">
        <span className="psp__stack-card-eyebrow">
          <FontAwesomeIcon icon={icon} /> {eyebrow}
        </span>
        <h5 className="psp__stack-card-title">{title}</h5>
        <span className="psp__stack-card-count">{items.length}</span>
      </div>
      {empty ? (
        <p className="psp__stack-card-empty">
          {isActive ? "No promotions are running right now." : "No upcoming promotions scheduled."}
        </p>
      ) : (
        <ul className="psp__stack-rows">
          {derivedActive && (
            <li className="psp__stack-row psp__stack-row--derived">
              <span className="psp__stack-row-icon">
                <FontAwesomeIcon icon={tierIcon(derivedActive.type)} />
              </span>
              <div className="psp__stack-row-body">
                <span className="psp__stack-row-title">
                  {PROMOTION_TIERS[derivedActive.type]?.label || derivedActive.type}
                  <span className="psp__stack-row-derived-tag">legacy</span>
                </span>
                <span className="psp__stack-row-range">
                  Active on this listing (no stack record).
                </span>
              </div>
            </li>
          )}
          {items.map((s) => (
            <StackRow
              key={s.id}
              stack={s}
              tz={tz}
              variant={variant}
              onCancel={onCancel}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function StackRow({ stack, tz, variant, onCancel }) {
  const now = Date.now();
  const startMs = tsToMs(stack.startAt);
  const endMs = tsToMs(stack.endAt);
  const isActive = variant === "active";
  const countdown = isActive
    ? `ends in ${formatCountdown(endMs - now)}`
    : `starts in ${formatCountdown(startMs - now)}`;
  const tierLabel = stack.type === "custom"
    ? "Custom range"
    : (PROMOTION_TIERS[stack.type]?.label || stack.type);
  return (
    <li className="psp__stack-row">
      <span className="psp__stack-row-icon">
        <FontAwesomeIcon icon={stack.type === "custom" ? faCalendarPlus : tierIcon(stack.type)} />
      </span>
      <div className="psp__stack-row-body">
        <span className="psp__stack-row-title">
          {tierLabel}
          {stack.count > 1 ? <span className="psp__stack-row-count">× {stack.count}</span> : null}
          {stack.recurrence?.rrule && (
            <span className="psp__stack-row-repeat" title={`Repeats (${stack.recurrence.rrule})`}>
              <FontAwesomeIcon icon={faSync} /> Repeats
            </span>
          )}
        </span>
        <span className="psp__stack-row-range">
          <strong>{isActive ? "Started" : "Starts"}:</strong> {formatTs(stack.startAt, tz)}
          {"  •  "}
          <strong>{isActive ? "Ends" : "Ends"}:</strong> {formatTs(stack.endAt, tz)}
        </span>
        <span className="psp__stack-row-meta">
          <span className="psp__stack-row-cost">{stack.tokenCost} Doubloons</span>
        </span>
      </div>
      <div className="psp__stack-row-side">
        <span className="psp__stack-countdown">
          <FontAwesomeIcon icon={faClock} /> {countdown}
        </span>
        {onCancel && (
          <button
            type="button"
            className="psp__stack-cancel"
            onClick={() => onCancel(stack)}
            aria-label="Cancel stack"
            disabled={isActive}
            title={isActive ? "Active stacks must run their course" : "Cancel this scheduled stack"}
          >
            <FontAwesomeIcon icon={faTimesCircle} /> Cancel
          </button>
        )}
      </div>
    </li>
  );
}

function PastStackList({ items, tz }) {
  return (
    <ul className="psp__stack-rows psp__stack-rows--past">
      {items.map((s) => (
        <li key={s.id} className="psp__stack-row psp__stack-row--past">
          <span className="psp__stack-row-icon">
            <FontAwesomeIcon icon={s.type === "custom" ? faCalendarPlus : tierIcon(s.type)} />
          </span>
          <div className="psp__stack-row-body">
            <span className="psp__stack-row-title">
              {s.type === "custom" ? "Custom range" : (PROMOTION_TIERS[s.type]?.label || s.type)}
              {s.status === "cancelled" && <span className="psp__stack-row-cancelled-tag">cancelled</span>}
            </span>
            <span className="psp__stack-row-range">
              {formatTs(s.startAt, tz)} → {formatTs(s.endAt, tz)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ----- helpers -----

function formatTs(ts, tz) {
  const ms = tsToMs(ts);
  if (!ms) return "—";
  return formatInUserTz(new Date(ms), tz, { dateStyle: "medium", timeStyle: "short" });
}
